import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuthStore } from '@/stores/auth';
import { LoadingDots } from '@/components/common/LoadingDots';

export function AuthGuard() {
  const { isAuthenticated, isInitialized, initAuth, user } = useAuthStore();
  const [isChecking, setIsChecking] = useState(!isInitialized);
  const location = useLocation();

  useEffect(() => {
    if (!isInitialized) {
      void initAuth().finally(() => setIsChecking(false));
    }
  }, [isInitialized, initAuth]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)]">
        <LoadingDots size="large" />
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Block unverified email users — but allow through if already on /verify-email
  if (user?.email_verified === false && location.pathname !== '/verify-email') {
    return <Navigate to="/verify-email" replace />;
  }

  // Hard-gate users who still owe a legal agreement (per-user ToU; org NDA/DPA for owners).
  const isAgreementRoute = location.pathname === '/agreements';
  if (user?.email_verified !== false && user?.needs_agreements && !isAgreementRoute) {
    return <Navigate to="/agreements" replace />;
  }

  // Redirect users who haven't completed onboarding (no env or env not provisioned).
  // Uses server-side `needs_onboarding` flag which checks both active_environment_id
  // and whether the environment has a tier set — covers email/password, OAuth, and
  // re-registration after account deletion.
  //
  // Agreements take priority: while a user still owes a legal agreement they are held
  // on /agreements, so the onboarding redirect must NOT fire there — otherwise the two
  // gates bounce /agreements → /onboarding → /agreements forever (the agreements gate
  // re-fires on /onboarding). Once agreements are accepted, onboarding resumes normally.
  const isOnboardingRoute = location.pathname === '/onboarding' || location.pathname.startsWith('/p/');
  if (user?.email_verified !== false && !user?.needs_agreements && user?.needs_onboarding && !isOnboardingRoute) {
    return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
}
